import React, { useState, useEffect } from 'react';
import { X, Printer, QrCode, Phone, Mail, MapPin, Maximize2, Home, CheckCircle } from 'lucide-react';
import QRCode from 'qrcode';
import { formatPrice, AGENT_INFO } from '../constants';
import Button from './ui/Button';

const PosterModal = ({ property, isOpen, onClose }) => {
    const [qrCodeUrl, setQrCodeUrl] = useState('');

    useEffect(() => {
        if (isOpen && property) {
            const propertyWebUrl = `${window.location.origin}/#/property/${property.id}`;
            QRCode.toDataURL(propertyWebUrl, {
                width: 250,
                margin: 1,
                color: {
                    dark: '#002B5B',
                    light: '#FFFFFF'
                }
            })
                .then((url) => setQrCodeUrl(url))
                .catch((err) => console.error('QR Code error:', err));
        }
    }, [isOpen, property]);

    if (!isOpen || !property || property?.status === 'Location') return null;

    const handlePrint = () => {
        window.print();
    };

    const isTerrain = (property?.type || '').toLowerCase().includes('terrain');
    const rawFeatures = Array.isArray(property?.features) ? property.features : [];
    const featuresList = [...new Set(rawFeatures)].filter(Boolean).slice(0, 6);

    const mainImage = property?.image_url || property?.image || '';
    const additionalImages = (Array.isArray(property?.images) ? property.images : [])
        .filter(img => img && img !== mainImage)
        .slice(0, 3);

    // Bedrooms count - direct fields only, no deduction
    const bedroomsCount = (() => {
        const directKeys = [property?.bedrooms, property?.nb_chambres, property?.chambres, property?.nb_bedrooms, property?.bedroom_count, property?.rooms];
        for (const k of directKeys) {
            if (k !== undefined && k !== null && k !== '' && Number(k) > 0) {
                return Number(k);
            }
        }
        return 'N/C';
    })();

    // Robust DPE fallback
    const dpeDisplay = (() => {
        const val = property?.dpe_energy || property?.dpe || property?.dpe_letter || property?.class_energy || property?.energy_class;
        if (val && typeof val === 'string' && val.trim()) return val.trim().toUpperCase();
        if (typeof val === 'number') return String(val);
        const descText = `${property?.title || ''} ${property?.description || ''} ${(property?.features || []).join(' ')}`;
        const descMatch = descText.match(/DPE\s*:?\s*([A-G])/i) || descText.match(/Classe\s*Énergie\s*:?\s*([A-G])/i);
        if (descMatch) return descMatch[1].toUpperCase();
        return 'En cours';
    })();

    const agentName = property?.agent_firstname 
        ? `${property.agent_firstname.trim().charAt(0).toUpperCase() + property.agent_firstname.trim().slice(1).toLowerCase()} ${property.agent_lastname ? property.agent_lastname.trim() : ''}`.trim() 
        : (AGENT_INFO.name || "Borbiconi Immobilier");
    const agentPhoto = property?.agent_photo || AGENT_INFO.photo;
    const agentPhone = property?.agent_phone || AGENT_INFO.phone;
    const agentEmail = property?.agent_email || AGENT_INFO.email;

    const isMale = (agentName || '').toLowerCase().includes('christophe') || (property?.agent_firstname || '').toLowerCase().includes('christophe');
    const roleTitle = isMale ? 'Agent Immobilier' : 'Conseillère Immobilier';
    const expertTagline = isMale ? 'Votre Expert Immobilier de Proximité' : 'Votre Experte Immobilière de Proximité';

    return (
        <div className="poster-modal-backdrop fixed inset-0 z-50 overflow-y-auto bg-black/70 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 print:p-0 print:bg-white print:static print:inset-auto">
            {/* Control Bar (Hidden when printing) */}
            <div className="fixed top-4 right-4 flex items-center gap-3 z-50 print:hidden">
                <Button
                    onClick={handlePrint}
                    className="bg-[#002B5B] hover:bg-[#001B3B] text-white font-bold px-4 py-2 text-xs rounded-xl shadow-lg flex items-center gap-2"
                >
                    <Printer size={16} />
                    <span>Imprimer / Télécharger PDF</span>
                </Button>

                <button
                    onClick={onClose}
                    className="w-10 h-10 bg-white hover:bg-gray-100 text-gray-700 rounded-full flex items-center justify-center shadow-lg transition-all"
                >
                    <X size={20} />
                </button>
            </div>

            {/* A4 Document Container */}
            <div className="poster-modal-card w-full max-w-3xl max-h-[85vh] bg-white rounded-2xl shadow-2xl overflow-y-auto print:overflow-hidden print:max-h-none print:shadow-none print:w-full print:max-w-none print:rounded-none my-auto">
                <div id="printable-poster" className="p-5 sm:p-6 print:p-10 bg-white text-gray-900 font-sans flex flex-col justify-between box-border min-h-0 print:min-h-0 print:h-[297mm] print:w-[210mm] print:box-border">

                    {/* Top Content Group */}
                    <div>
                        {/* Poster Header */}
                        <div className="flex items-center justify-between pb-3 print:pb-4 border-b-2 border-[#002B5B] mb-3 print:mb-5">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 print:w-12 print:h-12 bg-[#002B5B] text-white rounded-xl flex items-center justify-center font-black text-base print:text-xl tracking-tighter shadow-sm">
                                    SCBI
                                </div>
                                <div>
                                    <h2 className="text-base print:text-lg font-black text-[#002B5B] tracking-tight uppercase leading-none">BORBICONI IMMOBILIER</h2>
                                    <p className="text-[10px] print:text-xs text-orange-600 font-bold uppercase tracking-wider mt-0.5">{agentName} — {roleTitle}</p>
                                </div>
                            </div>

                            <div className="text-right">
                                <span className="inline-block px-2.5 py-0.5 print:px-3 print:py-1 bg-orange-100 text-orange-700 font-black text-[11px] print:text-xs uppercase rounded-full tracking-wider border border-orange-200">
                                    {property.type || 'À Vendre'}
                                </span>
                                {property.netty_ref && (
                                    <p className="text-[9px] print:text-[10px] font-mono text-gray-400 mt-0.5">Réf: #{property.netty_ref}</p>
                                )}
                            </div>
                        </div>

                        {/* Title & Price */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 print:gap-4 mb-3 print:mb-5">
                            <div>
                                <h1 className="text-xl print:text-2xl font-black text-[#002B5B] tracking-tight leading-tight">{property.title}</h1>
                                <p className="text-[11px] print:text-xs font-semibold text-gray-500 flex items-center gap-1 mt-0.5">
                                    <MapPin size={13} className="text-orange-500" />
                                    {property.city} ({property.postal_code || property.zipcode || '54'})
                                </p>
                            </div>

                            <div className="bg-[#002B5B] text-white px-4 py-2 print:px-5 print:py-2.5 rounded-xl print:rounded-2xl text-center sm:text-right shadow-md shrink-0">
                                <p className="text-[8px] print:text-[9px] uppercase font-bold text-blue-200 tracking-widest">Prix FAI</p>
                                <p className="text-xl sm:text-2xl print:text-3xl font-black">{formatPrice(property.price)}</p>
                            </div>
                        </div>

                        {/* Images Section: 1 Main Photo (Full Width) + 3 Thumbnails (Full Width Grid) */}
                        <div className="space-y-2 print:space-y-3 mb-3 print:mb-5">
                            {/* Photo 1: Main Photo - Full Width */}
                            <div className="w-full h-44 sm:h-52 print:h-64 rounded-xl print:rounded-2xl overflow-hidden shadow-sm bg-gray-100 border border-gray-100">
                                <img
                                    src={mainImage}
                                    alt={property.title}
                                    className="w-full h-full object-cover"
                                />
                            </div>

                            {/* Photo 2, 3, 4: 3 Thumbnails Grid - Full Width */}
                            {additionalImages.length > 0 && (
                                <div className="grid grid-cols-3 gap-2 print:gap-3">
                                    {additionalImages.map((img, idx) => (
                                        <div key={idx} className="h-20 sm:h-24 print:h-28 rounded-lg print:rounded-xl overflow-hidden bg-gray-100 border border-gray-100 shadow-sm">
                                            <img src={img} alt={`Photo ${idx + 2}`} className="w-full h-full object-cover" />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Property Specs Badges (Adapted for Terrain vs Maison/Appartement) */}
                        {isTerrain ? (
                            <div className="grid grid-cols-3 gap-2 print:gap-3 mb-3 print:mb-4 text-center">
                                <div className="bg-blue-50/60 border border-blue-100 p-2 print:p-2.5 rounded-xl">
                                    <p className="text-[8px] print:text-[9px] uppercase font-bold text-gray-400">Surface Terrain</p>
                                    <p className="text-xs print:text-sm font-black text-[#002B5B]">{property.surface_terrain || property.surface || 'N/C'} m²</p>
                                </div>
                                <div className="bg-blue-50/60 border border-blue-100 p-2 print:p-2.5 rounded-xl">
                                    <p className="text-[8px] print:text-[9px] uppercase font-bold text-gray-400">Nature du bien</p>
                                    <p className="text-xs print:text-sm font-black text-[#002B5B]">Terrain à bâtir</p>
                                </div>
                                <div className="bg-blue-50/60 border border-blue-100 p-2 print:p-2.5 rounded-xl">
                                    <p className="text-[8px] print:text-[9px] uppercase font-bold text-gray-400">Secteur</p>
                                    <p className="text-xs print:text-sm font-black text-orange-600 truncate">{property.city || 'Longwy'}</p>
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-3 gap-2 print:gap-3 mb-3 print:mb-4 text-center">
                                <div className="bg-blue-50/60 border border-blue-100 p-2 print:p-2.5 rounded-xl">
                                    <p className="text-[8px] print:text-[9px] uppercase font-bold text-gray-400">Surface Habitable</p>
                                    <p className="text-xs print:text-sm font-black text-[#002B5B]">{property.surface || 'N/C'} m²</p>
                                </div>
                                <div className="bg-blue-50/60 border border-blue-100 p-2 print:p-2.5 rounded-xl">
                                    <p className="text-[8px] print:text-[9px] uppercase font-bold text-gray-400">Chambres</p>
                                    <p className="text-xs print:text-sm font-black text-[#002B5B]">{bedroomsCount}</p>
                                </div>
                                <div className="bg-blue-50/60 border border-blue-100 p-2 print:p-2.5 rounded-xl">
                                    <p className="text-[8px] print:text-[9px] uppercase font-bold text-gray-400">DPE</p>
                                    <p className="text-xs print:text-sm font-black text-orange-600">{dpeDisplay}</p>
                                </div>
                            </div>
                        )}

                        {/* Features / Prestations (Jardin, Garage, Terrasse, etc.) */}
                        {featuresList.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 print:gap-2 mb-3 print:mb-4">
                                {featuresList.map((feat, idx) => (
                                    <span key={idx} className="inline-flex items-center gap-1 print:gap-1.5 px-2.5 py-0.5 print:px-3 print:py-1 bg-gray-100 text-gray-800 text-[9px] print:text-[10px] font-bold rounded-md print:rounded-lg border border-gray-200">
                                        <CheckCircle size={10} className="text-orange-500" /> {feat}
                                    </span>
                                ))}
                            </div>
                        )}

                        {/* Description */}
                        <div className="bg-gray-50 border border-gray-100 rounded-xl print:rounded-2xl p-3 print:p-4 mb-3 print:mb-5">
                            <h4 className="text-[10px] print:text-[11px] font-black uppercase text-[#002B5B] tracking-wider mb-0.5 print:mb-1">Descriptif du bien</h4>
                            <p className="text-[11px] print:text-xs text-gray-700 leading-relaxed line-clamp-2 print:line-clamp-4">
                                {property.description || `Superbe opportunité immobilière proposée par ${agentName}. Contactez-nous pour visiter ce bien et obtenir l'ensemble des informations détaillées.`}
                            </p>
                        </div>
                    </div>

                    {/* Footer Block with Contact & QR Code */}
                    <div className="pt-3 print:pt-4 border-t-2 border-[#002B5B] flex items-center justify-between gap-3 print:gap-4">
                        <div className="flex items-center gap-2.5 print:gap-3">
                            <img
                                src={agentPhoto}
                                alt={agentName}
                                className="w-12 h-12 print:w-14 print:h-14 rounded-full object-cover object-top border-2 border-orange-500 shadow-md shrink-0"
                            />
                            <div>
                                <h4 className="font-black text-[#002B5B] text-xs print:text-sm leading-tight">{agentName}</h4>
                                <p className="text-[10px] print:text-[11px] font-semibold text-gray-500 mb-0.5 print:mb-1">{expertTagline}</p>
                                <div className="flex items-center gap-2.5 print:gap-3 text-[11px] print:text-xs font-bold text-[#002B5B]">
                                    <span className="flex items-center gap-1"><Phone size={11} className="text-orange-500" /> {agentPhone}</span>
                                    <span className="flex items-center gap-1"><Mail size={11} className="text-orange-500" /> {agentEmail}</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 print:gap-3 text-right shrink-0">
                            <div>
                                <p className="text-[8px] print:text-[9px] font-black uppercase text-[#002B5B] tracking-wider">Fiche Web & Photos</p>
                                <p className="text-[7px] print:text-[8px] text-gray-400">Scannez pour visiter en ligne</p>
                            </div>
                            {qrCodeUrl ? (
                                <img src={qrCodeUrl} alt="QR Code Fiche Bien" className="w-14 h-14 print:w-16 print:h-16 rounded-lg print:rounded-xl border border-gray-200 shadow-sm" />
                            ) : (
                                <div className="w-14 h-14 print:w-16 print:h-16 bg-gray-100 rounded-lg print:rounded-xl flex items-center justify-center text-gray-400">
                                    <QrCode size={20} />
                                </div>
                            )}
                        </div>
                    </div>

                </div>
            </div>

            {/* Print Styles Injection */}
            <style>{`
                @page {
                    size: A4 portrait;
                    margin: 0;
                }
                @media print {
                    html, body {
                        background: white !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        width: 210mm !important;
                        height: 297mm !important;
                        overflow: hidden !important;
                    }
                    .poster-modal-backdrop {
                        position: static !important;
                        background: transparent !important;
                        backdrop-filter: none !important;
                        padding: 0 !important;
                        margin: 0 !important;
                        overflow: visible !important;
                        display: block !important;
                    }
                    .poster-modal-card {
                        max-height: none !important;
                        overflow: visible !important;
                        box-shadow: none !important;
                        border-radius: 0 !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        width: 210mm !important;
                        height: 297mm !important;
                    }
                    body * {
                        visibility: hidden !important;
                    }
                    #printable-poster, #printable-poster * {
                        visibility: visible !important;
                    }
                    #printable-poster {
                        position: absolute !important;
                        left: 0 !important;
                        top: 0 !important;
                        width: 210mm !important;
                        height: 297mm !important;
                        margin: 0 !important;
                        padding: 12mm 15mm !important;
                        box-sizing: border-box !important;
                        background: white !important;
                        display: flex !important;
                        flex-direction: column !important;
                        justify-content: space-between !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default PosterModal;
